import {
  parseVariableDefinitions,
  resolveMustacheString,
  type ImageBlock,
} from '@blogforge/shared';
import { canvasBlockStyle, cn } from '../../helpers';
import { ImageUploader } from '../ImageUploader';
import { useEditorStore } from '../../stores/editorStore';
import type { DragPath } from '../../types';

export function ImageRenderer({
  block,
  path,
  rawDefinitions,
}: {
  block: ImageBlock;
  path: DragPath;
  rawDefinitions: unknown;
}) {
  const definitions = rawDefinitions ? parseVariableDefinitions(rawDefinitions) : null;

  const context: Record<string, any> = {};

  if (definitions) {
    for (const [name, value] of Object.entries(definitions)) {
      if (value.default) {
        context[name] = value.default;
      }
    }
  }

  const { src, srcIsMustache } = block.attrs;
  const updateBlock = useEditorStore((s) => s.updateBlock);

  const resolvedSrc = srcIsMustache ? resolveMustacheString(src, context, { escape: false }) : src;

  // The email renderer splits the block's style bag the same way: the wrapper
  // takes the padding, margins, border and shadow, while the picture itself
  // takes the corner radius and the min/max size limits — so the canvas frames
  // and caps the image exactly where the message does. A limit on the wrapper
  // would only cap the frame and let the picture spill out of it.
  const { borderRadius, objectFit, maxHeight, maxWidth, minHeight, minWidth, ...wrapperStyle } =
    canvasBlockStyle(block.style);

  // object-fit only shows when the picture is stretched into a box of its own
  // shape, so a block with a height fills that box; without one the image
  // keeps its natural aspect ratio. An unset fit keeps the old defaults.
  const fillsBox = !!(block.attrs?.fullHeight || block.attrs?.height);
  const fit =
    objectFit ?? (block.attrs?.fullWidth || block.attrs?.fullHeight ? 'cover' : 'contain');

  return (
    <div className="text-center" style={wrapperStyle}>
      {resolvedSrc ? (
        <div
          className={cn('relative', {
            'w-full': block.attrs?.fullWidth,
            'h-full': block.attrs?.fullHeight,
            'min-h-[4px]': !block.attrs?.fullHeight,
          })}
          style={{
            width: block.attrs?.width && !block.attrs?.fullWidth ? block.attrs.width : undefined,
            height:
              block.attrs?.height && !block.attrs?.fullHeight ? block.attrs.height : undefined,
          }}
        >
          <img
            src={resolvedSrc}
            alt={block.attrs?.alt || ''}
            className={cn('block w-full', fillsBox ? 'h-full' : 'h-auto')}
            style={{ borderRadius, objectFit: fit, maxHeight, maxWidth, minHeight, minWidth }}
          />
        </div>
      ) : (
        <ImageUploader
          onSuccess={(url) => updateBlock(block.id, { attrs: { ...block.attrs, src: url } }, path)}
          variables={{
            skipOptimize: block.attrs.skipOptimize ? '1' : '0',
          }}
        />
      )}
      {block.attrs.caption && <p className="doc-muted mt-2 text-xs">{block.attrs.caption}</p>}
    </div>
  );
}
